import { useState, useCallback, useEffect } from "react";
import hrCalendarService from "../services/hrCalendarService";
import { SetAuditTrail, SetAuditTrailBulk } from "../../components/Reports/AuditTrailReportEmployeeSetup";

export const useHRCalendar = ({ showGeneralMessage, isMainModule = true }) => {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState([]);
  const [activeData, setActiveData] = useState([]);
  const [currentYearData, setCurrentYearData] = useState(null);
  const [filterData, setFilterData] = useState([]);

  // const { showGeneralMessage, showMessage, message, isSuccess } = useGeneralMessage(); // must be used outside/ much better is inside the module so we can reuse the main version of this shit

  /**
   * HELPER: Converts string "true"/"false" to actual booleans
   * This prevents C# backend errors.
   */
  const sanitizePayload = (payload) => {
    if (!payload || typeof payload !== "object") return payload;

    const cleanPayload = { ...payload };
    Object.keys(cleanPayload).forEach((key) => {
      if (cleanPayload[key] === "true") {
        cleanPayload[key] = true;
      } else if (cleanPayload[key] === "false") {
        cleanPayload[key] = false;
      }
    });
    return cleanPayload;
  };

  const fetchHRCalendars = useCallback(async () => {
    setLoading(true);
    try {
      const res = await hrCalendarService.getAll();
      const responseData = res.data || [];
      setData(responseData);
      setFilterData(responseData);
    } catch (err) {
      console.error("Failed to fetch HR calendars", err);
      showGeneralMessage("Failed to load HR calendars", false);
    } finally {
      setLoading(false);
    }
  }, [showGeneralMessage]);

  useEffect(() => {
    if (!isMainModule) return;

    fetchHRCalendars();
  }, [fetchHRCalendars, isMainModule]);

  const fetchActiveHRCalendars = useCallback(async () => {
    setLoading(true);
    try {
      const res = await hrCalendarService.getActive();
      const responseData = res.data || [];
      setActiveData(responseData);
    } catch (err) {
      console.error("Failed to fetch active HR calendars", err);
      showGeneralMessage("Failed to load active HR calendars", false);
    } finally {
      setLoading(false);
    }
  }, [showGeneralMessage]);

  useEffect(() => {
    if (!isMainModule) return;

    fetchActiveHRCalendars();
  }, [fetchActiveHRCalendars, isMainModule]);

  const fetchCurrentYear = useCallback(async () => {
    setLoading(true);
    try {
      const res = await hrCalendarService.getCurrentYear();
      const responseData = res.data || null;
      setCurrentYearData(responseData);
    } catch (err) {
      console.error("Failed to fetch current year", err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!isMainModule) return;

    fetchCurrentYear();
  }, [fetchCurrentYear, isMainModule]);

  const handleAction = async (actionFn, auditData, ...args) => {
    setLoading(true);
    try {
      const res = await actionFn(...args);

      if (Array.isArray(auditData)) {
        await SetAuditTrailBulk({ data: auditData });
      } else if (auditData) {
        await SetAuditTrail(auditData);
      }

      showGeneralMessage(res.data?.message || "Action successful", true);
      await fetchHRCalendars();
      await fetchActiveHRCalendars();
      await fetchCurrentYear();
      return { success: true, data: res.data };
    } catch (err) {
      const errorMsg = err.response?.data?.message || "An error occurred";
      showGeneralMessage(errorMsg, false);
      return { success: false };
    } finally {
      setLoading(false);
    }
  };

  const searchData = (value) => {
    if (!value) {
      setData(filterData);
      return;
    }
    const lower = value.toLowerCase();
    setData(filterData.filter(f =>
      f.id?.toString().includes(lower) ||
      f.year?.toString().includes(lower)
    ));
  };

  return {
    loading,
    data,
    activeData,
    currentYearData,
    searchData,
    // We sanitize the payload 'p' before sending it to the service
    createHRCalendar: (p, audit) => handleAction(hrCalendarService.create, audit, sanitizePayload(p)),
    updateHRCalendar: (id, p, audit) => handleAction(hrCalendarService.update, audit, id, sanitizePayload(p)),
    deleteHRCalendar: (id) => handleAction(hrCalendarService.delete, null, id),
    refresh: fetchHRCalendars,
    fetchActiveHRCalendars,
    fetchCurrentYear,
  };
};